import React, { useEffect, useMemo, useState } from 'react';
import { plannedDetails } from './plannedSegments';
import { useAuth } from '../../context/AuthContext';
import { canRead, normalizeAppRole } from '../../utils/roles';
import RRHHNativeShell from './RRHHNativeShell';
import { rrhhCoreGet } from './rrhhCoreApi';
import {
  dateKey,
  formatDisplayDate,
  formatDisplayTime,
  formatHours,
  formatMonthLabel,
  monthKey,
  monthRange,
  normalizeStatus,
  pairMarksByStartDay,
  pairTotalHours,
  personDisplayName,
  addMonths,
} from './rrhhUtils';
import './RRHHNative.css';

function canSelectPerson(user) {
  const role = normalizeAppRole(user?.role);
  return role === 'super_admin'
    || role === 'admin_rrhh'
    || canRead(user, 'rrhh.jornales')
    || canRead(user, 'rrhh.dashboard')
    || canRead(user, 'rrhh.configuracion');
}

function statusBadge(state) {
  const normalized = normalizeStatus(state || 'PENDIENTE');
  const kind = normalized === 'APROBADA' || normalized === 'VALIDADA'
    ? 'badge-green'
    : ['NO_APROBADA', 'RECHAZADA'].includes(normalized) ? 'badge-red' : 'badge-yellow';
  return <span className={`badge ${kind}`}>{normalized.replaceAll('_', ' ')}</span>;
}

function MarkPoint({ label, mark, groupDay }) {
  const timestamp = mark?.fecha_hora || (mark?.fecha && mark?.hora ? `${mark.fecha} ${mark.hora}` : '');
  const hasMark = Boolean(timestamp);
  const markDay = dateKey(timestamp);
  const suffix = markDay && markDay !== groupDay ? ` ${markDay.slice(8, 10)}/${markDay.slice(5, 7)}` : '';
  return (
    <div className="rrhh-native-point">
      <small>{label}{suffix}</small>
      <strong>{hasMark ? formatDisplayTime(timestamp) : 'Sin marca'}</strong>
      {hasMark && (mark?.actividad_ubicacion || mark?.actividad) && <span className="text-sm text-dim">{mark.actividad_ubicacion || mark.actividad}</span>}
    </div>
  );
}

function segmentTotalHours(segment) {
  if (!segment?.entrada_hora || !segment?.salida_hora) return null;
  return pairTotalHours({
    entry: { fecha_hora: `${segment.entrada_fecha || ''} ${segment.entrada_hora}` },
    exit: { fecha_hora: `${segment.salida_fecha || ''} ${segment.salida_hora}` },
  });
}

function planSummary(journal) {
  const status = normalizeStatus(journal?.estado_turno || 'SIN_TURNO');
  if (status !== 'NORMAL') return status.replaceAll('_', ' ');
  return plannedDetails(journal) || 'Horario sin definir';
}

export default function MisMarcasPage() {
  const { user } = useAuth();
  const canViewPeopleSelector = canSelectPerson(user);
  const [people, setPeople] = useState([]);
  const [selectedPersonId, setSelectedPersonId] = useState('');
  // La misma ventana que aplica el backend: el mes corriente, y el anterior
  // durante los primeros seis días. Se recalcula acá sólo para saber qué
  // ofrecer; quien manda es el servidor, que rechaza los meses cerrados.
  const mesesVisibles = useMemo(() => {
    const hoy = new Date();
    const actual = monthKey(hoy);
    return hoy.getDate() <= 6 ? [addMonths(actual, -1), actual] : [actual];
  }, []);
  const [visibleMonth, setVisibleMonth] = useState(() => mesesVisibles[mesesVisibles.length - 1]);
  const puedeRetroceder = mesesVisibles.indexOf(visibleMonth) > 0;
  const puedeAvanzar = mesesVisibles.indexOf(visibleMonth) < mesesVisibles.length - 1;
  const [marks, setMarks] = useState([]);
  const [approvedJournals, setApprovedJournals] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      rrhhCoreGet('/mi-persona'),
      canViewPeopleSelector ? rrhhCoreGet('/personas?scope=all') : Promise.resolve([]),
    ])
      .then(([ownPerson, rows]) => {
        if (!active) return;
        const peopleById = new Map();
        [ownPerson, ...rows].forEach((person) => {
          if (person && Number(person.activo) !== 0) peopleById.set(String(person.id), person);
        });
        const activePeople = [...peopleById.values()].sort((left, right) => (
          personDisplayName(left).localeCompare(personDisplayName(right), 'es')
        ));
        setPeople(activePeople);
        setSelectedPersonId(String(ownPerson.id));
      })
      .catch((err) => setError(err.message || 'No se pudo cargar el personal'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [canViewPeopleSelector]);

  const selectedPerson = useMemo(
    () => people.find((person) => String(person.id) === String(selectedPersonId)) || null,
    [people, selectedPersonId]
  );

  useEffect(() => {
    if (!selectedPerson) return;
    let active = true;
    const { from, to } = monthRange(visibleMonth);
    setLoading(true);
    setError('');
    Promise.all([
      rrhhCoreGet(`/marcas?persona_id=${selectedPerson.id}&desde=${from}&hasta=${to}`),
      rrhhCoreGet(`/mis-marcas/jornales?persona_id=${selectedPerson.id}&desde=${from}&hasta=${to}&todos=1`),
      rrhhCoreGet(`/operaciones?persona=${selectedPerson.id}&desde=${from}&hasta=${to}`),
    ])
      .then(([monthMarks, monthJournals, monthOperations]) => {
        if (!active) return;
        setMarks(monthMarks);
        setApprovedJournals(monthJournals);
        setOperations(Array.isArray(monthOperations) ? monthOperations : []);
      })
      .catch((err) => setError(err.message || 'No se pudieron cargar tus marcas'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [selectedPerson, visibleMonth]);

  const personName = personDisplayName(selectedPerson);
  const markGroups = useMemo(() => {
    const journalsByDay = new Map();
    approvedJournals.forEach((journal) => {
      const segments = Array.isArray(journal.tramos) ? journal.tramos : [];
      const hasMarks = segments.some((segment) => segment.entrada_id || segment.salida_id || segment.entrada_hora || segment.salida_hora);
      if (hasMarks && String(journal.fecha || '').startsWith(visibleMonth)) journalsByDay.set(journal.fecha, journal);
    });

    // Las marcas sueltas también conservan su lugar si todavía no están
    // asociadas a una jornada canónica.
    pairMarksByStartDay(marks).forEach((pair) => {
      if (!pair.dayKey.startsWith(visibleMonth) || journalsByDay.has(pair.dayKey)) return;
      journalsByDay.set(pair.dayKey, {
        fecha: pair.dayKey,
        estado_turno: 'SIN_TURNO',
        estado_aprobacion: pair.entry?.estado_aprobacion || pair.exit?.estado_aprobacion || 'PENDIENTE',
        tramos: [{
          entrada_id: pair.entry?.id || null,
          entrada_fecha: dateKey(pair.entry?.fecha_hora),
          entrada_hora: pair.entry ? formatDisplayTime(pair.entry.fecha_hora) : null,
          entrada_actividad: pair.entry?.actividad_ubicacion || null,
          salida_id: pair.exit?.id || null,
          salida_fecha: dateKey(pair.exit?.fecha_hora),
          salida_hora: pair.exit ? formatDisplayTime(pair.exit.fecha_hora) : null,
          salida_actividad: pair.exit?.actividad_ubicacion || null,
        }],
      });
    });

    return [...journalsByDay.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [approvedJournals, marks, visibleMonth]);
  const validatedHours = useMemo(() => (
    Math.round(approvedJournals
      .filter((row) => normalizeStatus(row.estado_aprobacion) === 'APROBADA')
      .reduce((total, row) => total + Number(row.horas_trabajadas || 0), 0) * 100) / 100
  ), [approvedJournals]);
  const operationGroups = useMemo(() => {
    const grouped = operations.reduce((acc, operation) => {
      const day = dateKey(operation.fecha_hora);
      if (!acc.has(day)) acc.set(day, []);
      acc.get(day).push(operation);
      return acc;
    }, new Map());
    return [...grouped.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [operations]);

  // Sólo se dibuja si hay a dónde ir: con un mes solo, dos flechas muertas
  // sugieren que hay historial escondido y no lo hay.
  const navegadorDeMes = mesesVisibles.length > 1 ? (
    <div className="mm-month-nav">
      <button type="button" className="btn btn-ghost" disabled={!puedeRetroceder}
        onClick={() => setVisibleMonth(addMonths(visibleMonth, -1))} aria-label="Mes anterior">‹</button>
      <span className="mm-month-label">{formatMonthLabel(visibleMonth)}</span>
      <button type="button" className="btn btn-ghost" disabled={!puedeAvanzar}
        onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))} aria-label="Mes siguiente">›</button>
    </div>
  ) : null;

  // Sin navegador ni selector el contenedor vacío seguía ocupando su hueco.
  const actions = navegadorDeMes || canViewPeopleSelector ? (
    <div className="mm-actions">
      {navegadorDeMes}
      {canViewPeopleSelector && (
        <select className="form-input" value={selectedPersonId} onChange={(event) => setSelectedPersonId(event.target.value)}>
          {people.map((person) => (
            <option key={person.id} value={person.id}>{personDisplayName(person)}</option>
          ))}
        </select>
      )}
    </div>
  ) : null;

  return (
    <RRHHNativeShell
      className="mm-page"
      title="Mis marcas"
      // El mes lo canta el navegador cuando está; repetirlo acá era leerlo dos
      // veces en dos renglones seguidos.
      subtitle={navegadorDeMes ? personName || 'Persona' : `${personName || 'Persona'} · ${formatMonthLabel(visibleMonth)}`}
      actions={actions}
    >
      {error && <div className="alert alert-error">{error}</div>}

      <section className="rrhh-native-grid">
        <article className="rrhh-native-card rrhh-native-metric">
          <small>Horas validadas</small>
          <strong>{formatHours(validatedHours)} h</strong>
        </article>
      </section>

      <div className="rrhh-native-toolbar">
        <strong>Operaciones del mes</strong>
      </div>
      <section className="rrhh-native-list">
        {!operations.length && <div className="rrhh-native-card rrhh-native-empty">Sin operaciones registradas para este mes.</div>}
        {operationGroups.map(([day, dayOperations]) => (
          <div className="rrhh-native-day-group" key={day}>
            <div className="rrhh-native-day-head"><span>{formatDisplayDate(day)}</span></div>
            {dayOperations.map((operation) => (
              <article className="rrhh-native-operation-row" key={operation.id}>
                <div className="rrhh-native-operation-main">
                  <strong>{operation.referencia || 'Sin referencia'}</strong>
                  <div className="rrhh-native-operation-meta">
                    <span>{operation.tarifa_categoria || operation.tipo_operacion || 'Sin categoría'} · {operation.franja || 'Sin franja'}</span>
                  </div>
                  {operation.observacion && <span className="rrhh-native-operation-comment">{operation.observacion}</span>}
                </div>
                <div>{statusBadge(operation.estado)}</div>
              </article>
            ))}
          </div>
        ))}
      </section>

      <div className="rrhh-native-toolbar">
        <strong>Marcas del mes</strong>
        {loading && <span className="text-sm text-dim">Cargando...</span>}
      </div>

      <section className="rrhh-native-list">
        {!markGroups.length && <div className="rrhh-native-card rrhh-native-empty">Sin marcas registradas para este mes.</div>}
        {markGroups.map(([day, journal]) => (
          <div className="rrhh-native-day-group" key={day}>
            <div className="rrhh-native-day-head">
              <span>{formatDisplayDate(day)}</span>
            </div>
            <article className="rrhh-native-mark-day">
              <div className="rrhh-native-mark-plan">
                <small>Plan semanal</small>
                <strong>{planSummary(journal)}</strong>
              </div>
              {(journal.tramos || []).map((segment, index) => (
                <div className="rrhh-native-mark-pair" key={`${day}-${index}`}>
                  <MarkPoint label="Entrada" mark={{ fecha: segment.entrada_fecha, hora: segment.entrada_hora, actividad: segment.entrada_actividad }} groupDay={day} />
                  <MarkPoint label="Salida" mark={{ fecha: segment.salida_fecha, hora: segment.salida_hora, actividad: segment.salida_actividad }} groupDay={day} />
                  <div className="rrhh-native-mark-footer">
                    <span>Total: <strong>{segmentTotalHours(segment) === null ? 'Abierto' : `${formatHours(segmentTotalHours(segment))} h`}</strong></span>
                    {statusBadge(journal.estado_aprobacion)}
                  </div>
                </div>
              ))}
            </article>
          </div>
        ))}
      </section>
    </RRHHNativeShell>
  );
}
